import { Autocomplete, AutocompleteChangeReason, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useController } from 'react-hook-form';

import { environment } from '@env';

import { GeocodeAPIV6Response } from '@/types/mapbox';

import FormField, { FormFieldProps, getControllerProps, getFormFieldProps } from './FormField';

interface Props extends FormFieldProps {
  placeholder?: string;
  allowManualEdit?: boolean;
}

const FormAddressAutocomplete = forwardRef<HTMLDivElement, Props>(
  (
    { color = 'secondary', placeholder = 'Enter address', allowManualEdit = true, ...props },
    ref
  ) => {
    const {
      field: { value, onChange, onBlur: fieldOnBlur },
    } = useController({
      name: props.name,
      control: props.control,
      defaultValue: props.defaultValue,
    });
    const [innerValue, setInnerValue] = useState(value ?? props.defaultValue ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedFromDropdown, setSelectedFromDropdown] = useState(false);
    const [manuallyEdited, setManuallyEdited] = useState(false);
    const lastManualValue = useRef<string | null>(null);
    const optionsRef = useRef<GeocodeAPIV6Response['features']>([]);

    // Only update inner value from external changes if:
    // 1. We haven't manually edited the value
    // 2. OR the external value has changed significantly (not just a minor formatting change)
    useEffect(() => {
      if (
        value &&
        value !== innerValue &&
        (!manuallyEdited || (lastManualValue.current && value !== lastManualValue.current))
      ) {
        setInnerValue(value);
        // Reset manual edit flag if value was changed externally
        if (manuallyEdited && lastManualValue.current && value !== lastManualValue.current) {
          setManuallyEdited(false);
        }
      }
    }, [value, innerValue, manuallyEdited]);

    const fetchSuggestionsQuery = useQuery({
      enabled: innerValue.length > 2 && isOpen && !manuallyEdited,
      queryKey: ['GET', 'suggestions', innerValue],
      queryFn: async () => {
        const response = await axios.get<GeocodeAPIV6Response>(
          'https://api.mapbox.com/search/geocode/v6/forward?',
          {
            params: new URLSearchParams({
              q: innerValue,
              access_token: environment.mapboxApiKey,
              autocomplete: 'true',
              country: 'US',
              format: 'geojson',
              language: 'en-US',
              limit: '5',
              types: 'address',
            }),
          }
        );

        optionsRef.current = response.data.features;

        return response.data;
      },
    });

    const options = useMemo(() => {
      if (fetchSuggestionsQuery.isError) return [];
      if (fetchSuggestionsQuery.isFetching) return optionsRef.current;

      return fetchSuggestionsQuery.data?.features ?? [];
    }, [
      fetchSuggestionsQuery.data?.features,
      fetchSuggestionsQuery.isError,
      fetchSuggestionsQuery.isFetching,
    ]);

    const handleOnChange = (
      event: React.SyntheticEvent,
      _value: GeocodeAPIV6Response['features'][0] | null,
      reason: AutocompleteChangeReason
    ) => {
      if (reason === 'clear') {
        onChange('');
        setInnerValue('');
        setSelectedFromDropdown(false);
        setManuallyEdited(false);
        lastManualValue.current = null;
        return;
      }

      const addressValue = _value?.properties.full_address ?? '';
      onChange(addressValue);
      setInnerValue(addressValue);
      setSelectedFromDropdown(true);
      setManuallyEdited(false);
      lastManualValue.current = null;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInnerValue(newValue);

      // If we're allowing manual editing, update the form value
      // and track that this is a manual edit
      if (allowManualEdit) {
        if (selectedFromDropdown || manuallyEdited) {
          setManuallyEdited(true);
          lastManualValue.current = newValue;
        }
        // Always propagate the change to the form
        onChange(newValue);
      } else if (!selectedFromDropdown) {
        // If nothing was selected yet, just update the inner value
        onChange(newValue);
      }
    };

    // When the field loses focus, make sure we preserve the manually edited value
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Always update the form with our current value
      onChange(innerValue);

      // Store the last manual value for comparison
      if (manuallyEdited) {
        lastManualValue.current = innerValue;
      }

      // Call the field's onBlur if it exists
      if (fieldOnBlur) {
        fieldOnBlur();
      }
    };

    return (
      <FormField {...getFormFieldProps(props)} ref={ref}>
        <Controller
          {...getControllerProps(props)}
          render={({ field }) => (
            <Autocomplete
              disabled={props.disabled}
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              onChange={handleOnChange}
              isOptionEqualToValue={(option, _value) =>
                option.properties.full_address === _value.properties.full_address
              }
              filterOptions={_options => _options}
              getOptionLabel={option => option.properties.full_address}
              options={options}
              value={null}
              inputValue={innerValue}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                  },
                  '&.Mui-focused': {
                    outline: 'none !important',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                    borderWidth: 1,
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
                  },
                },
                ...props.sx,
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  {...field}
                  size={props.size}
                  placeholder={placeholder}
                  value={innerValue}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: 'text.primary',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&:focus, &.Mui-focused': {
                        outline: 'none !important',
                      },
                    },
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isOpen && fetchSuggestionsQuery.isFetching && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              slotProps={{
                paper: {
                  elevation: 3,
                  sx: {
                    mt: 1,
                    borderRadius: 1,
                    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                    '& .MuiAutocomplete-listbox': {
                      padding: '8px 0',
                    },
                    '& .MuiAutocomplete-option': {
                      padding: '10px 16px',
                      fontSize: '1rem',
                      fontWeight: 400,
                      '&[aria-selected="true"]': {
                        backgroundColor: '#F3F3F3 !important',
                        color: 'text.primary',
                        fontWeight: 400,
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    },
                  },
                },
              }}
            />
          )}
        />
      </FormField>
    );
  }
);

export default FormAddressAutocomplete;
